"use client";

import { useState } from "react";
import Link from "next/link";
import { Check } from "lucide-react";
import styles from "./PricingPlans.module.css";

const plans = [
  {
    name: "Basic",
    description: "Perfect for small bed & breakfasts and independent motels.",
    monthlyPrice: 49,
    yearlyPrice: 39,
    features: [
      "Up to 20 rooms",
      "Basic Booking Engine",
      "Standard Support",
      "Web Builder (Templates only)",
    ],
    cta: "Get Started",
    ctaLink: "#get-started",
    isPopular: false,
  },
  {
    name: "Standard",
    description: "Ideal for growing hotels looking to increase direct bookings.",
    monthlyPrice: 99,
    yearlyPrice: 79,
    features: [
      "Up to 50 rooms",
      "Advanced Booking Engine",
      "Channel Manager Connection",
      "Priority Email Support",
      "Custom Domain Integration",
    ],
    cta: "Get Started",
    ctaLink: "#get-started",
    isPopular: true,
  },
  {
    name: "Premium",
    description: "Advanced tools for established hotels and boutique chains.",
    monthlyPrice: 199,
    yearlyPrice: 159,
    features: [
      "Up to 150 rooms",
      "Everything in Standard",
      "Revenue Management Tools",
      "24/7 Phone Support",
      "White-label Web Builder",
    ],
    cta: "Get Started",
    ctaLink: "#get-started",
    isPopular: false,
  },
  {
    name: "Enterprise",
    description: "Custom solutions for large hotel chains and resort groups.",
    monthlyPrice: "Custom",
    yearlyPrice: "Custom",
    features: [
      "Unlimited rooms",
      "Dedicated Account Manager",
      "Custom API Integrations",
      "On-premise deployment options",
      "Advanced Security & SLA",
    ],
    cta: "Contact Sales",
    ctaLink: "#contact-sales",
    isPopular: false,
  },
];

export default function PricingPlans() {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.toggleContainer}>
          <span className={`${styles.toggleLabel} ${!isYearly ? styles.active : ""}`}>Monthly</span>
          <div
            className={`${styles.toggleSwitch} ${isYearly ? styles.toggled : ""}`}
            onClick={() => setIsYearly(!isYearly)}
            role="switch"
            aria-checked={isYearly}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsYearly(!isYearly);
              }
            }}
          >
            <div className={styles.toggleHandle}></div>
          </div>
          <span className={`${styles.toggleLabel} ${isYearly ? styles.active : ""}`}>Yearly</span>
          <span className={styles.saveBadge}>Save 20%</span>
        </div>

        <div className={styles.plansGrid}>
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`${styles.card} ${plan.isPopular ? styles.popularCard : ""}`}
            >
              {plan.isPopular && <div className={styles.popularBadge}>Most Popular</div>}
              <h3 className={styles.planName}>{plan.name}</h3>
              <p className={styles.planDescription}>{plan.description}</p>
              
              <div className={styles.priceBlock}>
                {plan.monthlyPrice === "Custom" ? (
                  <span className={styles.price}>Custom</span>
                ) : (
                  <>
                    <span className={styles.currency}>$</span>
                    <span className={styles.price}>
                      {isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                    </span>
                    <span className={styles.period}>/mo</span>
                  </>
                )}
              </div>

              <ul className={styles.featuresList}>
                {plan.features.map((feature, fIndex) => (
                  <li key={fIndex} className={styles.featureItem}>
                    <Check className={styles.checkIcon} size={18} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                href={plan.ctaLink}
                className={`${styles.btn} ${plan.isPopular ? styles.btnPrimary : styles.btnOutline}`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
